import React from "react";
import { Card, CardContent, Box, Typography, Skeleton } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import PropTypes from "prop-types";
import AutoMode from "./AutoModeConfig";

const Legend = ({ color, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Box
      sx={{
        width: 14,
        height: 14,
        backgroundColor: color,
        borderRadius: 0.5,
      }}
    />
    <Typography variant="caption" sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

const OverpressureEventsCard = ({
  data,
  renderHeader,
  loading,
  mode,
  setMode,
  handleStartCountdown,
}) => {
  const hasRealData = Object.keys(data || {}).length > 0;

  const getLastThreeMonths = () => {
    const months = [];
    const now = new Date();

    for (let i = 2; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7); // e.g., "2025-08"
      months.push(key);
    }

    return months;
  };

  const monthOrder = getLastThreeMonths();

  const monthLabels = Object.fromEntries(
    monthOrder.map((monthStr) => {
      const [year, month] = monthStr.split("-");
      const date = new Date(Number(year), Number(month) - 1);
      const shortName = date.toLocaleString("default", { month: "short" });
      return [monthStr, shortName];
    })
  );

  const dataForChart = hasRealData
    ? monthOrder.map((key) => ({
        month: monthLabels[key] || key,
        v1: data?.[key]?.v1 || 0,
        v2: data?.[key]?.v2 || 0,
      }))
    : [
        { month: "May", v1: 0, v2: 0 },
        { month: "June", v1: 0, v2: 0 },
        { month: "July", v1: 1, v2: 1 },
      ];

  return (
    <Card sx={cardStyle}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 }, height: "100%" }}>
        {renderHeader("Overpressure Data", "overpressure")}

        {mode === "auto" ? (
          <AutoMode
            metricType="overpressure"
            setMode={setMode}
            onApply={(metricType, duration) => {
              handleStartCountdown(
                metricType,
                parseInt(duration.replace("m", ""))
              );
            }}
          />
        ) : loading ? (
          <Box
            sx={{
              width: "100%",
              height: 220,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Skeleton variant="rectangular" width="90%" height={180} />
          </Box>
        ) : (
          <>
            <Box sx={{ width: "100%", height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dataForChart}
                  margin={{ top: 10, left: -30, right: 10, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    axisLine
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    axisLine
                    tickLine={false}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="v1"
                    name="V1"
                    fill="#FFCC00"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="v2"
                    name="V2"
                    fill="#FF4C4C"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: -6,
                }}
              >
                <Legend color="#FFCC00" label="V1" />
                <Legend color="#FF4C4C" label="V2" />
              </Box>
            </Box>

            {!hasRealData && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  color: "text.secondary",
                }}
              >
                Sample Data Displayed
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OverpressureEventsCard;

const cardStyle = {
  borderRadius: 2,
  boxShadow: 1,
  backgroundColor: "#F9FAFB",
  width: "100%",
  minWidth: 0,
  maxWidth: 320,
  p: 1,
  height: 260,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
};

OverpressureEventsCard.propTypes = {
  data: PropTypes.object.isRequired,
  renderHeader: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  setMode: PropTypes.func.isRequired,
  handleStartCountdown: PropTypes.func.isRequired,
};

Legend.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};
